import { CircularProgress, Stack, TextField, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { MealPreferenceItem, RoutineActivityRecurring } from '@demind-inc/core';

import {
  useAssistantPref,
  useAuthContext,
  useSchedulerRoutine,
  useUpdateRoutine,
} from '../../../../../data-access';
import { AppSwitch } from '../../../../common';
import { MealSettingItem } from '../AIAssistantMealSettings/MealSettingItem';
import { defaultRecurring } from '../../../../Scheduler/AIScheduler/reducer';
import { isCustomRecurring } from '../../../../Scheduler/common';
import { findWorkoutRoutineActivity } from '../../../../../helpers/allPlanSuggestions.helpers';
import './AIAssistantWorkoutSettings.scss';

const DEFAULT_WORKOUT_START_TIME = '18:00';
const DEFAULT_WORKOUT_END_TIME = '19:00';
const DEFAULT_WORKOUT_DURATION_MIN = 60;

const AIAssistantWorkoutSettings = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { workoutPref, updateWorkoutPref, isPreferenceLoading } = useAssistantPref();
  const { routine, isLoading: isRoutineLoading } = useSchedulerRoutine({
    routineId: user.routineId ?? '',
  });
  const { updateRoutine } = useUpdateRoutine();
  const [note, setNote] = useState((workoutPref as any)?.note ?? '');

  const workoutActivity = useMemo(
    () => findWorkoutRoutineActivity(routine?.routineActivities),
    [routine?.routineActivities]
  );

  const workoutPreferenceItem = useMemo<MealPreferenceItem | null>(() => {
    if (!workoutActivity) {
      return null;
    }
    return {
      earliestTime: workoutActivity.startTime || DEFAULT_WORKOUT_START_TIME,
      latestTime: workoutActivity.endTime || DEFAULT_WORKOUT_END_TIME,
      durationMin: workoutActivity.durationMin || DEFAULT_WORKOUT_DURATION_MIN,
      // The routine activity's recurrence may be a custom rule (e.g. "every 2 weeks"); the
      // simple weekday-toggle editor only understands the day-map shape, so fall back to a
      // default map rather than misrepresenting a custom rule as a plain weekly recurrence.
      recurring: isCustomRecurring(workoutActivity.recurring)
        ? defaultRecurring
        : workoutActivity.recurring,
    };
  }, [workoutActivity]);

  const debouncedUpdateWorkoutRoutine = useMemo(
    () =>
      debounce(
        (patch: {
          startTime?: string;
          endTime?: string;
          durationMin?: number;
          recurring?: RoutineActivityRecurring;
        }) => {
          if (!user.routineId || !user.preferenceId || !workoutActivity?.routineActivityId) {
            return;
          }
          updateRoutine({
            routineId: user.routineId,
            preferenceId: user.preferenceId,
            activityId: workoutActivity.routineActivityId,
            routineActivity: patch,
          });
        },
        1000
      ),
    [updateRoutine, user.routineId, user.preferenceId, workoutActivity?.routineActivityId]
  );

  useEffect(() => {
    return () => {
      debouncedUpdateWorkoutRoutine.cancel();
    };
  }, [debouncedUpdateWorkoutRoutine]);

  const debouncedSaveNote = useMemo(
    () =>
      debounce((value: string) => {
        updateWorkoutPref({ note: value });
      }, 2000),
    [updateWorkoutPref]
  );

  useEffect(() => {
    return () => {
      debouncedSaveNote.cancel();
    };
  }, [debouncedSaveNote]);

  const hasAutoScheduleToggle = useMemo(
    () => workoutPref && typeof (workoutPref as any).disableAutoSchedule === 'boolean',
    [workoutPref]
  );
  const [isAutoScheduleEnabled, setIsAutoScheduleEnabled] = useState(
    hasAutoScheduleToggle ? !(workoutPref as any).disableAutoSchedule : true
  );
  const [excludePlanContents, setExcludePlanContents] = useState(
    (workoutPref as any)?.excludePlanContents ?? false
  );

  useEffect(() => {
    setNote((workoutPref as any)?.note ?? '');
  }, [workoutPref]);

  useEffect(() => {
    setExcludePlanContents((workoutPref as any)?.excludePlanContents ?? false);
  }, [(workoutPref as any)?.excludePlanContents]);

  useEffect(() => {
    if (!hasAutoScheduleToggle) {
      return;
    }
    setIsAutoScheduleEnabled(!(workoutPref as any).disableAutoSchedule);
  }, [hasAutoScheduleToggle, (workoutPref as any)?.disableAutoSchedule]);

  if (isPreferenceLoading || isRoutineLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" flex={1}>
        <CircularProgress size={16} />
      </Stack>
    );
  }

  return (
    <Stack gap={2} className="ai-assistant-workout-settings">
      <Stack gap={1}>
        <Typography className="ai-assistant-workout-settings__note-label" variant="subtitle1">
          {t('settings.common.note')}
        </Typography>
        <TextField
          className="ai-assistant-workout-settings__note"
          placeholder={t('settings.aiAssistant.workoutNotePlaceholder')}
          value={note}
          onChange={(e) => {
            const value = e.target.value;
            setNote(value);
            debouncedSaveNote(value);
          }}
          multiline
          minRows={1}
          size="small"
          fullWidth
          variant="standard"
          InputProps={{ disableUnderline: true }}
        />
      </Stack>
      {hasAutoScheduleToggle && (
        <div className="ai-assistant-workout-settings__item-content">
          <div className="ai-assistant-workout-settings__item-content__title">
            {t('settings.aiAssistant.autoWorkoutSuggestions')}
          </div>

          <AppSwitch
            checked={isAutoScheduleEnabled}
            onChange={(_e, checked) => {
              setIsAutoScheduleEnabled(checked);
              updateWorkoutPref({ disableAutoSchedule: !checked });
            }}
          />
        </div>
      )}

      <div className="ai-assistant-workout-settings__item-content">
        <div className="ai-assistant-workout-settings__item-content__title">
          {t('settings.aiAssistant.includeWorkoutPlanContents')}
        </div>
        <AppSwitch
          checked={!excludePlanContents}
          onChange={(_e, checked) => {
            setExcludePlanContents(!checked);
            updateWorkoutPref({ excludePlanContents: !checked });
          }}
        />
      </div>

      {workoutPreferenceItem && (
        <Stack gap={1}>
          <Typography className="ai-assistant-workout-settings__item-title" variant="subtitle1">
            {t('settings.aiAssistant.meals.workout')}
          </Typography>
          <MealSettingItem
            mealPreferenceItem={workoutPreferenceItem}
            mealType="workout"
            onTimeChange={(start, end) => {
              debouncedUpdateWorkoutRoutine({ startTime: start, endTime: end });
            }}
            onDurationChange={(duration) => {
              debouncedUpdateWorkoutRoutine({ durationMin: duration });
            }}
            onRecurringChange={(recurring) => {
              debouncedUpdateWorkoutRoutine({ recurring });
            }}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default AIAssistantWorkoutSettings;
